import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Button, Pill, SkeletonSet } from '@cwos/ui-admin';
import { LoadFailure } from '../LoadFailure.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { useNav } from '../navigation.js';
import { AGENTS_CONFIGURE_KEY, agentAuthority, authorityOpen } from './consentLogic.js';
import { agents } from './agentsClient.js';
import {
  activitySentence,
  auditActorSentence,
  auditVerbSentence,
  authorityFreshnessSentence,
  authorityHeadline,
  authorityReason,
  authoritySourceSentence,
  deployerSentence,
  keyStandingLabel,
  keyStandingNote,
  keyStandingPill,
  lastRunSentence,
  runStatusLabel,
  runStatusPill,
  statusLabel,
  statusPill,
  triggerIsInert,
  triggerSentence,
} from './agentsLogic.js';

/**
 * ONE AGENT: what it is, what it would be allowed to do, what it has done,
 * and how to stop it.
 *
 * ===========================================================================
 * THE AUTHORITY PANEL IS THE POINT OF THIS PAGE
 * ===========================================================================
 * An agent's permission profile is a stored list of keys, and rendering it as
 * "what this agent can do" would be showing the customer something untrue.
 * Authority is decided at FIRE time: the runtime re-derives the deployer's
 * live holds at run start, at resume, at the moment an approval arrives, and
 * again immediately before EVERY action — so the interval between the last
 * check and the executor call is zero. A deployer who lost a permission on
 * Monday refuses Tuesday's queued action, with the profile untouched.
 *
 * So this page asks the server for a VERDICT, not a profile. The server
 * re-runs the same derivation the engine reduces to a boolean and hands back
 * the reasoning: the source of the authority, the deployer's standing, and a
 * per-key standing including `not_evaluated` for keys the derivation never
 * reached. Everything here is in the future conditional — "would" — because
 * that is the only tense the data supports.
 *
 * AND IT SAYS WHEN IT WAS WORKED OUT. Without that line a reader takes the
 * panel for a settings screen, and the one property that matters most — that
 * it is re-derived, and can change without anyone touching the agent — is
 * invisible.
 *
 * ===========================================================================
 * THE CONTROLS ARE THREE KEYS, NOT ONE
 * ===========================================================================
 * Pause/resume needs `agents:pause`. Emergency stop needs
 * `agents:emergency_stop`, its own key, because the two acts are not the
 * same: pause is reversible from this screen and stop is not. There is no
 * `agents:manage` and the screen must not imply one — a reader holding pause
 * and not stop sees the pause button and an explanation of the missing one,
 * never a single greyed-out "manage" affordance that suggests both travel
 * together.
 *
 * A control the reader cannot use is ABSENT, not disabled (default-deny), but
 * the STOP control is the exception and says so: an operator looking for the
 * emergency stop and finding nothing concludes the product has none. So its
 * absence is stated in words, with the key named, which is the same choice
 * `NoPermission` makes for a whole screen.
 */

const ACTIVITY_PAGE = 50;
const RUNS_PAGE = 25;

export function AgentDetail() {
  const params = useParams({ strict: false }) as { deploymentId?: string };
  const deploymentId = params.deploymentId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNav();
  const permissions = usePermissions();
  const [stopArmed, setStopArmed] = useState(false);
  const [controlError, setControlError] = useState<unknown>(null);

  /**
   * PAUSE AND STOP, EACH WITH FOUR ANSWERS.
   *
   * The two notes below name a permission the reader "does not hold", and both
   * used to be chosen by a default-deny boolean that is equally `false` when
   * `/auth/me` could not be re-read. Neither key gates this route — it is
   * `AGENTS_READ_KEY` — so a reader whose cached set carries THAT key is let
   * through `RequirePermission` under `isError` and is then told, from a set
   * nobody could refresh, that they hold neither control. On the emergency-stop
   * note that is the worst possible sentence to get wrong: the whole reason it
   * is written out rather than hidden is that an operator reads a missing stop
   * button as "this product does not have one", and telling them the permission
   * is not theirs when nobody could check is the same mistake with a citation.
   *
   * The booleans stay default-deny for the CONTROLS, which is correct: a button
   * must not appear until the answer is yes.
   */
  const pauseGate = authorityGate(permissions, 'agents:pause');
  const stopGate = authorityGate(permissions, 'agents:emergency_stop');
  const mayPause = gateOpen(pauseGate);
  const mayStop = gateOpen(stopGate);
  /**
   * RETUNING IS A THIRD KEY AND A DIFFERENT FRAME. `PATCH /api/agents/:id/
   * configuration` is `agents:configure`, and that guard is `can()` at
   * organization scope — an agent is an organization-level actor, so a
   * program-scoped grant is a 403 rather than a narrower answer. `gateOpen`
   * above reads `has()`, which is right for the two controls it gates because
   * this screen's own route is `agents:read`; it is the wrong instrument for a
   * door that refuses, so this one asks the question the door asks.
   */
  const mayConfigure = authorityOpen(agentAuthority(permissions, AGENTS_CONFIGURE_KEY));

  const detail = useQuery({
    queryKey: ['agents', 'detail', deploymentId],
    queryFn: () => agents.detail(deploymentId),
    enabled: deploymentId !== '',
  });

  const runs = useQuery({
    queryKey: ['agents', 'runs', deploymentId],
    queryFn: () => agents.runs(deploymentId, { limit: RUNS_PAGE }),
    enabled: deploymentId !== '',
  });

  const activity = useQuery({
    queryKey: ['agents', 'activity', deploymentId],
    queryFn: () => agents.activity(deploymentId, { limit: ACTIVITY_PAGE }),
    enabled: deploymentId !== '',
  });

  const afterControl = () => {
    setControlError(null);
    setStopArmed(false);
    void queryClient.invalidateQueries({ queryKey: ['agents'] });
  };

  const pause = useMutation({
    mutationFn: () => agents.pause(deploymentId),
    onSuccess: afterControl,
    onError: (err) => setControlError(err),
  });
  const resume = useMutation({
    mutationFn: () => agents.resume(deploymentId),
    onSuccess: afterControl,
    onError: (err) => setControlError(err),
  });
  const stop = useMutation({
    mutationFn: () => agents.emergencyStop(deploymentId),
    onSuccess: afterControl,
    onError: (err) => setControlError(err),
  });

  if (detail.isLoading) return <SkeletonSet surface="drawer" rows={8} />;
  if (detail.isError) {
    return (
      <LoadFailure
        error={detail.error}
        message="We could not load this agent."
        onRetry={() => void detail.refetch()}
        mainRead
        refusedHeading="You cannot open this agent"
      />
    );
  }

  const data = detail.data;
  if (!data) return null;
  const { agent, fireTimeAuthority: authority, executionBudget } = data;
  const now = Date.now();
  const reason = authorityReason(authority);
  const deployer = deployerSentence(authority);
  const activityPage = activity.data;

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <a className="rn-link" href="/agents">
        ← Agents
      </a>

      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
          <h1>{agent.name}</h1>
          <Pill variant={statusPill(agent.status)}>{statusLabel(agent.status)}</Pill>
        </div>
        <p className="rn-muted">{agent.objective}</p>
        <p className="rn-muted">
          {agent.definitionName} · {lastRunSentence(agent)}
        </p>
      </header>

      {/* ===== WHAT IT MAY DO WHEN IT RUNS ==================================== */}
      <section className="rn-card">
        <h2 className="rn-card-title">What it may do when it runs</h2>
        <p className="rn-strong">{authorityHeadline(authority)}</p>
        {reason ? <p>{reason}</p> : null}
        <p className="rn-muted">{authoritySourceSentence(authority)}</p>
        {deployer ? <p className="rn-muted">{deployer}</p> : null}

        <h3 className="rn-card-title">The permissions it was given</h3>
        <p className="rn-muted">
          These are the permissions delegated to this agent when it was set up. What each one would
          do at the next run is beside it — and that is the answer, not the list.
        </p>
        <ul className="rn-list">
          {authority.keys.map((entry) => {
            const note = keyStandingNote(entry.standing);
            return (
              <li key={entry.key} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
                  <code>{entry.key}</code>
                  <Pill variant={keyStandingPill(entry.standing)}>
                    {keyStandingLabel(entry.standing)}
                  </Pill>
                </div>
                {note ? <span className="rn-muted">{note}</span> : null}
              </li>
            );
          })}
        </ul>

        {/* THE LINE THAT STOPS THIS READING AS A SETTINGS SCREEN. */}
        <p className="rn-muted">{authorityFreshnessSentence(authority.evaluatedAt, now)}</p>
      </section>

      {/* ===== CONTROLS ====================================================== */}
      <section className="rn-card card-record">
        <h2 className="rn-card-title">Controls</h2>
        {controlError ? (
          <LoadFailure
            error={controlError}
            message="That did not go through. Nothing has changed."
            refusedHeading="You cannot do that to this agent"
          />
        ) : null}

        <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
          {mayPause && agent.status === 'ACTIVE' ? (
            <Button variant="secondary" onClick={() => pause.mutate()} disabled={pause.isPending}>
              Pause
            </Button>
          ) : null}
          {mayPause && agent.status === 'PAUSED' ? (
            <Button variant="secondary" onClick={() => resume.mutate()} disabled={resume.isPending}>
              Resume
            </Button>
          ) : null}
          {mayStop && agent.status !== 'DISABLED' ? (
            stopArmed ? (
              <>
                <Button
                  variant="destructive"
                  onClick={() => stop.mutate()}
                  disabled={stop.isPending}
                >
                  Stop it now
                </Button>
                <Button variant="quiet" onClick={() => setStopArmed(false)}>
                  Keep it running
                </Button>
              </>
            ) : (
              <Button variant="destructive" onClick={() => setStopArmed(true)}>
                Emergency stop
              </Button>
            )
          ) : null}
        </div>

        {/*
          CHANGING WHAT IT DOES IS NOT A CONTROL OVER WHETHER IT RUNS, and the
          two sit apart for that reason. The configuration door refuses an
          ACTIVE deployment outright — a run in flight holds a principal built
          from the profile the change would replace — so the link says which act
          has to come first rather than leading to a form that will refuse.
        */}
        {mayConfigure && agent.status !== 'DISABLED' ? (
          <p className="rn-muted">
            {agent.status === 'ACTIVE' ? (
              <>Pause it first, then change what it does.</>
            ) : (
              <a className="rn-link" href={`/agents/deploy?agent=${agent.id}`}>
                Change what this agent does
              </a>
            )}
          </p>
        ) : null}

        {stopArmed ? (
          <p>
            This stops the agent and ends every run of it that is under way. It cannot be started
            again from here — a stopped agent has to be set up afresh. Everything it has already
            done stays on the record.
          </p>
        ) : null}

        {/*
          THE MISSING CONTROL IS NAMED, not silently absent. Every other gated
          affordance in this app is simply hidden, because a hidden control
          asks no question. An emergency stop is the one control whose absence
          an operator will read as "this product does not have one", and they
          will read it that way in the situation where being wrong costs most.
        */}
        <GateNotice
          gate={stopGate}
          what="stop an agent"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <p className="rn-muted">
              Stopping an agent needs the <code>agents:emergency_stop</code> permission, which you
              do not hold. It is a separate permission from pausing on purpose: pausing can be
              undone from this screen and stopping cannot. Ask whoever administers your
              organization.
            </p>
          }
        />
        <GateNotice
          gate={pauseGate}
          what="pause and resume an agent"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <p className="rn-muted">
              Pausing and resuming needs the <code>agents:pause</code> permission, which you do not
              hold.
            </p>
          }
        />
      </section>

      {/* ===== WHAT MAKES IT RUN ============================================= */}
      <section className="rn-card">
        <h2 className="rn-card-title">What makes it run</h2>
        {agent.triggers.length === 0 ? (
          <p className="rn-muted">Nothing starts this agent. It will not run on its own.</p>
        ) : (
          <ul className="rn-list">
            {agent.triggers.map((trigger, index) => (
              <li key={`${trigger.kind}-${index}`}>
                {triggerSentence(trigger)}
                {triggerIsInert(trigger.kind) ? (
                  <span className="rn-muted">
                    {' '}
                    — this is configured and nothing in the product raises it, so it has never
                    started a run and will not.
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <p className="rn-muted">
          Budget: up to {executionBudget.maxCreditsPerRun} credits a run,{' '}
          {executionBudget.maxActionsPerRun} actions a run. A run that reaches a limit stops there.
        </p>
      </section>

      {/* ===== RUNS ========================================================== */}
      <section className="rn-card card-record">
        <h2 className="rn-card-title">Runs</h2>
        <ListState
          phase={listPhase(runs, runs.data?.length)}
          surface="table"
          rows={4}
          failedMessage="We could not load this agent’s runs. This is not a statement that it has none."
          onRetry={() => void runs.refetch()}
          empty={<p className="rn-muted">This agent has never run.</p>}
        >
          <ul className="rn-list">
            {(runs.data ?? []).map((run) => (
              <li key={run.id}>
                <button
                  type="button"
                  className="rn-link"
                  onClick={() => navigate(`/agents/runs/${run.id}`)}
                >
                  <Pill variant={runStatusPill(run.status)}>{runStatusLabel(run.status)}</Pill>{' '}
                  {run.startedAt ? new Date(run.startedAt).toLocaleString() : 'not started'} ·{' '}
                  {run.actionsCompleted} of {run.actionsAttempted} actions completed
                </button>
              </li>
            ))}
          </ul>
        </ListState>
      </section>

      {/* ===== THE AUDIT TRAIL =============================================== */}
      <section className="rn-card card-record">
        <h2 className="rn-card-title">Everything this agent has done</h2>
        <ListState
          phase={listPhase(activity, activityPage?.entries.length)}
          surface="table"
          rows={6}
          failedMessage="We could not load this agent’s history. This is not a statement that it has none."
          onRetry={() => void activity.refetch()}
          empty={<p className="rn-muted">{activitySentence({ count: 0, hasMore: false })}</p>}
        >
          <>
            <ul className="rn-list">
              {(activityPage?.entries ?? []).map((entry) => (
                <li key={entry.auditEventId} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                  <span className="rn-strong">{auditVerbSentence(entry.action)}</span>
                  <span className="rn-muted">
                    <time dateTime={new Date(entry.at).toISOString()}>
                      {new Date(entry.at).toLocaleString()}
                    </time>{' '}
                    · {auditActorSentence(entry)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="rn-muted">
              {activitySentence({
                count: activityPage?.entries.length ?? 0,
                hasMore: activityPage?.hasMore ?? false,
              })}
            </p>
          </>
        </ListState>
      </section>
    </article>
  );
}
